import { ItemImage } from '@components/CatalogElements';
import {
  ItemSearchInput,
  type ItemDropdownRow,
} from '@components/ItemSearchInput';
import { LoaderBar } from '@components/LoaderBar';
import TextCopyToClipboard from '@components/TextCopyToClipboard';
import { useRefreshSidebar } from '@contexts/sidebar';
import { BoldText, PageHeaderText, StyledCheckbox } from '@elements/Common';
import { defaultCurrencyMaskOptions } from '@elements/CustomMaskInput';
import {
  ControlledPhoneFormField,
  ControlledTextFormField,
  CountryDropdownFormField,
  FormHelpToolTip,
  MarketplacesDropdown,
  MaskedNumberFormField,
  MuteLabel,
  NoSpecialCharactedInput,
} from '@elements/FormField';
import { zodResolver } from '@hookform/resolvers/zod';
import { marketplaceRegionHook } from '@hooks/marketplace-region';
import { BrowserUtility } from '@utility/browser-utility';
import { CommonUtility } from '@utility/common';
import { ErrorMessageConstant, ItemStates } from '@utility/constants/common';
import OrdersService from '@utility/services/order';
import { ToastMessage } from '@utility/toast';
import padStart from 'lodash/padStart';
import random from 'lodash/random';
import lodashToString from 'lodash/toString';
import { Plus, X } from '@phosphor-icons/react';
import { Fragment, useEffect, useId, useMemo, useState } from 'react';
import {
  useFieldArray,
  useForm,
  useWatch,
  type Control,
  type FieldErrors,
} from 'react-hook-form';
import { z } from 'zod';
import { Button, Modal } from '@skupreme/shared/ui';
import { CustomerSearchInput } from '@components/CustomerSearchInput';
import { dropdownOptionsIncludeFlag } from '@elements/Flag';
import { StatusBox } from '@page-components/catalog';
import { useMarketPlaceDropdownHook } from '@hooks/marketplace';
import type { CustomerResult } from 'types/components/custom-search-input';
import { PasteAddressPPopup } from './PasteAddressPopup';
import {
  AppTableCell,
  AppTableRow,
  AppTableHeader,
  AppTableHeaderCell,
  AppTableBody,
  AppTableRoot,
} from '@components/AppTable';

/* Messages are the ones yup generated from the field paths. */
const requiredText = (path: string) =>
  z
    .string({ required_error: `${path} is a required field` })
    .min(1, `${path} is a required field`);

const positiveInteger = (path: string) =>
  z.coerce
    .number({ invalid_type_error: `${path} must be a number` })
    .int(`${path} must be an integer`)
    .positive(`${path} must be a positive number`);

/*
 * Unknown keys pass through, as yup let them: whatever the form holds goes to
 * the API as the recipient.
 */
const orderSchema = z
  .object({
    name: requiredText('name'),
    company: z.string().optional(),
    address_line_one: requiredText('address_line_one'),
    address_line_two: z.string().optional(),
    city: requiredText('city'),
    state: requiredText('state'),
    zip_code: requiredText('zip_code'),
    phone: z.string().optional(),
    country: requiredText('country'),
    /* An empty email is no email, as yup's `email()` read it. */
    email: z
      .union([z.literal(''), z.string().email('email must be a valid email')])
      .optional(),
    order_id: requiredText('order_id').max(
      30,
      'order_id must be at most 30 characters',
    ),
    marketplace_id: z.string().optional(),
    items: z
      .array(z.object({ quantity: positiveInteger('quantity') }).passthrough())
      .optional(),
    custom_items: z
      .array(
        z
          .object({
            item_name: z
              .string({ required_error: 'Item Name is required' })
              .min(1, 'Item Name is required'),
            price: z.coerce
              .number({ invalid_type_error: 'Price is required field' })
              .gt(0, 'price must be greater than 0'),
            quantity: z.coerce
              .number({ invalid_type_error: 'Quantity is required' })
              .int('quantity must be an integer')
              .positive('quantity must be a positive number'),
          })
          .passthrough(),
      )
      .optional(),
  })
  .passthrough();

/* The masked inputs hold strings; the schema coerces them on submit. */
type OrderFormValues = {
  name?: string;
  company?: string;
  address_line_one?: string;
  address_line_two?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  phone?: string;
  country?: string;
  email?: string;
  order_id?: string;
  marketplace_id?: string;
  items?: { quantity?: string | number }[];
  custom_items?: {
    item_name?: string;
    price?: string | number;
    quantity?: string | number;
  }[];
};

/* A shipping label's address, left in storage by the page that opened this. */
type StoredLabel = {
  name?: string;
  phone?: string;
  email?: string;
  address?: {
    addressLine1?: string;
    city?: string;
    stateOrRegion?: string;
    postalCode?: string;
    countryCode?: string;
  };
};

type CustomerAddress = NonNullable<CustomerResult['address']> & {
  addressLine2?: string;
  city?: string;
};

type ManualOrderRequest = {
  marketplace_id: string;
  recipient: Record<string, unknown>;
  order_id: string;
  items?: { item_id: string; quantity: number }[];
  custom_items?: { item_name: string; price: number; quantity: number }[];
};

const HEADER_TEXT = 'sk:text-xs sk:font-normal sk:text-fg-tertiary';

type RowErrors = FieldErrors<OrderFormValues>;

function RemoveButton({ onRemove }: { onRemove: () => void }) {
  return (
    <Button
      variant="ghost"
      shape="circle"
      aria-label="Remove line item"
      onPress={onRemove}
    >
      <X size={18} className="sk:text-fg-tertiary" />
    </Button>
  );
}

function ItemRow({
  item,
  index,
  errors,
  removeItem,
  control,
}: {
  item: ItemDropdownRow;
  index: number;
  errors: RowErrors;
  removeItem: () => void;
  control: Control<OrderFormValues>;
}) {
  return (
    <AppTableRow verticalAlign="middle">
      <AppTableCell>
        <div className="sk:flex sk:items-center sk:gap-4">
          <div className="sk:w-12.5 sk:min-w-12.5">
            <ItemImage size="mini" extraClasses="sk:w-[50px]!" item={item} />
          </div>
          <div className="sk:flex sk:flex-col">
            <div>
              {item?.short_title || item?.p_title}
              {item?.state === ItemStates.component && (
                <div className="sk:ml-2 sk:inline-block">
                  <StatusBox status="Component" minWidth="70px" />
                </div>
              )}
            </div>
            <div>
              {item?.p_sku && (
                <BoldText>
                  <TextCopyToClipboard text={item.p_sku} iconSize={14} />
                </BoldText>
              )}
            </div>
          </div>
        </div>
      </AppTableCell>
      <AppTableCell textAlign="center">
        {CommonUtility.currencyFormat(item?.p_price, item?.p_currency)}
      </AppTableCell>
      <AppTableCell textAlign="center">
        {CommonUtility.numberWithCommas(item?.inventory)}
      </AppTableCell>
      <AppTableCell>
        <MaskedNumberFormField
          error={errors?.items?.[index]?.quantity ?? null}
          name={`items[${index}].quantity`}
          required
          control={control}
          defaultValue=""
          placeholder="Qty"
          fieldClass="sk:mb-0!"
          // @ts-expect-error - TS2322: Type '{ error: any; name: string; required: true; control: any; defaultValue: st...
          minValue={0}
          maxValue={+(item?.inventory || 0)}
        />
      </AppTableCell>
      <AppTableCell textAlign="center">
        <RemoveButton onRemove={removeItem} />
      </AppTableCell>
    </AppTableRow>
  );
}

function UpdateCustomItem({
  item,
  errors,
  control,
  removeItem,
  index,
}: {
  item: {
    item_name?: string;
    price?: string | number;
    quantity?: string | number;
  };
  errors: RowErrors;
  control: Control<OrderFormValues>;
  removeItem: () => void;
  index: number;
}) {
  return (
    <AppTableRow verticalAlign="middle">
      <AppTableCell>
        <ControlledTextFormField
          error={errors?.custom_items?.[index]?.item_name || null}
          name={`custom_items[${index}].item_name`}
          control={control}
          required
          placeholder="Custom Line Item Name"
          fieldClass="sk:mb-0!"
          defaultValue={item?.item_name}
        />
      </AppTableCell>
      <AppTableCell width={2}>
        <MaskedNumberFormField
          control={control}
          defaultValue={item?.price}
          error={errors?.custom_items?.[index]?.price || null}
          maskOptions={defaultCurrencyMaskOptions}
          name={`custom_items[${index}].price`}
          placeholder="Price"
          required
          fieldClass="sk:mb-0!"
        />
      </AppTableCell>
      <AppTableCell />
      <AppTableCell>
        <MaskedNumberFormField
          control={control}
          defaultValue={item?.quantity}
          error={errors?.custom_items?.[index]?.quantity || null}
          maskOptions={{
            includeThousandsSeparator: true,
            thousandsSeparatorSymbol: ',',
          }}
          name={`custom_items[${index}].quantity`}
          placeholder="Qty"
          required
          fieldClass="sk:mb-0!"
        />
      </AppTableCell>
      <AppTableCell textAlign="center">
        <RemoveButton onRemove={removeItem} />
      </AppTableCell>
    </AppTableRow>
  );
}

type FieldRow = { id: string };

function Items({
  control,
  customItemRemove,
  customItemsFields,
  errors,
  fields,
  items,
  onResultClick,
  regionId,
  removeItem,
  isInternational,
}: {
  control: Control<OrderFormValues>;
  customItemRemove: (index: number) => void;
  customItemsFields: (FieldRow & {
    item_name?: string;
    price?: string | number;
    quantity?: string | number;
  })[];
  errors: RowErrors;
  fields: FieldRow[];
  items: ItemDropdownRow[];
  onResultClick: (item: ItemDropdownRow) => void;
  regionId?: string;
  removeItem: (index: number, id: string) => void;
  isInternational: boolean;
}) {
  const [searchResetKey, setSearchResetKey] = useState(0);
  const handleResultClick = (selected: ItemDropdownRow) => {
    onResultClick(selected);
    setSearchResetKey(k => k + 1);
  };
  const noResults = !(
    CommonUtility.isValidArray(fields) ||
    CommonUtility.isValidArray(customItemsFields)
  );

  return (
    <>
      <ItemSearchInput
        key={searchResetKey}
        currentItems={items}
        onResultClick={handleResultClick}
        regionId={regionId}
        removeSearchAfterSelect
        shouldRefreshOnRegionChange={false}
        top
        isInternational={isInternational}
      />
      {!noResults ? (
        <div className="sk:overflow-x-auto">
          <AppTableRoot basic="very">
            <AppTableHeader>
              <AppTableRow verticalAlign="middle">
                <AppTableHeaderCell width={7}>
                  <span className={HEADER_TEXT}>Name</span>
                </AppTableHeaderCell>
                <AppTableHeaderCell width={3} textAlign="center">
                  <span className={HEADER_TEXT}>Price</span>
                </AppTableHeaderCell>
                <AppTableHeaderCell width={3} textAlign="center">
                  <span className={HEADER_TEXT}>Available Units</span>
                </AppTableHeaderCell>
                <AppTableHeaderCell width={3} textAlign="center">
                  <span className={HEADER_TEXT}>Quantity</span>
                </AppTableHeaderCell>
                <AppTableHeaderCell width={2} textAlign="center" />
              </AppTableRow>
            </AppTableHeader>
            <AppTableBody>
              {fields?.map((field, index) => (
                <Fragment key={field.id}>
                  <ItemRow
                    index={index}
                    errors={errors}
                    item={items[index]}
                    control={control}
                    removeItem={() => removeItem(index, items[index]._id)}
                  />
                </Fragment>
              ))}
              {customItemsFields?.map((f, index) => (
                <Fragment key={f.id}>
                  <UpdateCustomItem
                    control={control}
                    errors={errors}
                    index={index}
                    item={f}
                    removeItem={() => customItemRemove(index)}
                  />
                </Fragment>
              ))}
            </AppTableBody>
          </AppTableRoot>
        </div>
      ) : (
        <div className="sk:mb-4" />
      )}
    </>
  );
}

type MarketplaceOptionLike = {
  value?: string;
  text?: string;
  name?: string;
  item?: { name?: string };
};

const codeOf = (o: MarketplaceOptionLike) =>
  o?.item?.name || o?.name || o?.text || '';
const isSkupreme = (s: string) => /^skupreme(_.*)?$/i.test(s || '');
const isSkupremeUS = (s: string) => /^skupreme(_?us)?$/i.test(s || '');

const DOMESTIC_COUNTRY_ID = '6012c24092485d8b30c764e7';

type ManualOrderPopupProps = {
  open: boolean;
  /** Called with the new order's id once saved, with nothing when cancelled. */
  closeModal: (id?: string) => void;
  /** Called when the dialog is dismissed without the Cancel flow. */
  closeManual: () => void;
  /** A customer to start from; its fields fill the form. */
  customer: OrderFormValues | null;
};

export function ManualOrderPopup({
  open,
  closeModal,
  closeManual: _closeManual,
  customer,
}: ManualOrderPopupProps) {
  const formId = useId();
  const { data: regionData = [] } = marketplaceRegionHook();
  const [showAddressPopup, setShowAddressPopup] = useState(false);
  const [items, setItems] = useState<ItemDropdownRow[]>([]);
  const [saving, setSaving] = useState(false);
  const refreshSidebar = useRefreshSidebar();
  const [countryCode, setCountryCode] = useState<string>();
  const [isInternational, setIsInternational] = useState(false);
  const { data: connectedMarketplaces = [] } =
    useMarketPlaceDropdownHook(false);

  const marketplaceOptions = useMemo(() => {
    if (!Array.isArray(connectedMarketplaces)) return [];

    const filtered = connectedMarketplaces.filter(opt => {
      const code = codeOf(opt);
      return !(isSkupreme(code) && !isSkupremeUS(code));
    });

    const mapped = filtered.map(opt => {
      const code = codeOf(opt);
      return isSkupremeUS(code) ? { ...opt, text: 'Manual' } : opt;
    });

    mapped.sort((a, b) => {
      const aIsManual = isSkupremeUS(codeOf(a));
      const bIsManual = isSkupremeUS(codeOf(b));
      if (aIsManual && !bIsManual) return -1;
      if (!aIsManual && bIsManual) return 1;
      return 0;
    });

    return mapped;
  }, [connectedMarketplaces]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
    setValue,
    register,
    watch,
  } = useForm<OrderFormValues>({
    resolver: zodResolver(orderSchema),
    shouldUnregister: true,
  });

  useEffect(() => {
    register('marketplace_id');
  }, [register]);

  useEffect(() => {
    if (!open) return;
    const current = getValues('marketplace_id');
    if (current) return;
    const manual = marketplaceOptions.find(o => isSkupremeUS(codeOf(o)));
    if (manual) {
      setValue('marketplace_id', String(manual.value), { shouldDirty: false });
    }
  }, [open, marketplaceOptions, getValues, setValue]);

  const label = BrowserUtility.getObj<StoredLabel>('label');
  const country = watch('country');

  useEffect(() => {
    if (!country) {
      setIsInternational(false);
      return;
    }
    setIsInternational(country !== DOMESTIC_COUNTRY_ID);
  }, [country]);

  useEffect(() => {
    if (label && regionData.length > 0) {
      const optFound = regionData?.find(
        x => x.name === label.address?.countryCode,
      );
      if (optFound) {
        setValue('country', optFound.value);
      }
    }
  }, [regionData, label, setValue]);

  useEffect(() => {
    if (open && customer && regionData.length > 0) {
      const temp = { ...customer };
      const tempCountry = regionData.find(x => x.name === customer.country);
      if (tempCountry) {
        temp.country = tempCountry.value;
      }
      reset(temp);
    }
  }, [customer, open, regionData, reset]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
    keyName: 'id',
  });

  const {
    fields: customItemsFields,
    append: customItemsAppend,
    remove: customItemRemove,
  } = useFieldArray({
    control,
    name: 'custom_items',
    keyName: 'id',
  });

  const close = (id?: string) => {
    reset({
      marketplace_id: '',
      custom_items: [],
      items: [],
    });
    setItems([]);
    setIsInternational(false);
    BrowserUtility.remove('label');
    closeModal(id);
  };

  const onSubmit = async (data: OrderFormValues) => {
    BrowserUtility.remove('label');

    try {
      setSaving(true);
      const {
        marketplace_id,
        order_id,
        items: lineItems,
        custom_items: customItems,
        ...recipient
      } = data;
      const reqData: ManualOrderRequest = {
        marketplace_id: marketplace_id || '',
        recipient,
        order_id: `OID-${order_id}`,
      };
      if (CommonUtility.isValidArray(lineItems)) {
        reqData.items = lineItems.map((item, index) => ({
          item_id: items[index]._id,
          quantity: Number(item.quantity),
        }));
      }

      if (CommonUtility.isValidArray(customItems)) {
        reqData.custom_items = customItems.map(d => ({
          item_name: d?.item_name || '',
          price: Number(d?.price || 0),
          quantity: Number(d?.quantity || 0),
        }));
      }

      const result = await OrdersService.add(reqData);

      refreshSidebar();

      ToastMessage.success('Order created successfully.');
      close(result?.data?._id);
    } catch (error) {
      ToastMessage.error(error?.error || ErrorMessageConstant.defaultAPIError);
    } finally {
      setSaving(false);
    }
  };

  const onResultClick = (item: ItemDropdownRow) => {
    append({ quantity: '' });
    setItems(prev => [...prev, { ...item }]);
  };

  const removeItem = (index: number, id: string) => {
    remove(index);
    setItems(prev => prev.filter(x => x._id !== id));
  };

  const extractAddress = (address: Record<string, unknown>) => {
    const obj: OrderFormValues & Record<string, unknown> = {
      ...getValues(),
    };
    for (const component in address) {
      if (Object.prototype.hasOwnProperty.call(address, component)) {
        if (component === 'country') {
          const region = regionData.find(x => x.value === address[component]);
          obj[component] = region?.value;
          continue;
        }
        if (address[component] !== null) {
          obj[component] = address[component];
        }
      }
    }
    reset(obj);
  };

  const onUserSelect = ({
    name = '',
    email = '',
    phone = '',
    _id,
    address = {},
  }: CustomerResult) => {
    if (_id) {
      const customerAddress: CustomerAddress = address;
      setValue('name', name);
      setValue('email', email);
      setValue('phone', phone);
      setValue('address_line_one', customerAddress?.addressLine1 || '');
      setValue('address_line_two', customerAddress?.addressLine2 || '');
      setValue('city', customerAddress?.city || '');
      setValue('state', customerAddress?.stateOrRegion || '');
      setValue('zip_code', customerAddress?.postalCode || '');

      if (
        customerAddress?.countryCode &&
        CommonUtility.isValidArray(regionData)
      ) {
        const selected = regionData.find(
          x => x.name === customerAddress.countryCode,
        );
        if (selected) {
          setValue('country', selected.value);
        }
      }
    }
  };

  const name = useWatch({ control, name: 'name' });

  const handleCountryChange = (item: { value?: string }) => {
    const selectedCountryCode = dropdownOptionsIncludeFlag(regionData)
      .find(cd => cd.value === item.value)
      ?.name?.toLowerCase();
    setCountryCode(selectedCountryCode);
  };

  return (
    /*
     * Not dismissable: a stray click or Esc would throw away a typed order.
     * The header's X and Cancel close it, through `close`.
     */
    <Modal
      isOpen={open}
      onOpenChange={() => {}}
      isDismissable={false}
      aria-label="Manual Order"
      size="xl"
      className="sk:max-h-full sk:overflow-y-auto"
      footer={
        <>
          <Button className="sk:mr-auto" onPress={() => close()}>
            Cancel
          </Button>
          <Button onPress={() => customItemsAppend({ item_name: '' })}>
            <Plus size={14} />
            Custom Line Item
          </Button>
          <Button
            variant="primary"
            type="submit"
            form={formId}
            isDisabled={
              !(
                CommonUtility.isValidArray(fields) ||
                CommonUtility.isValidArray(customItemsFields)
              ) || !country
            }
          >
            Save Order
          </Button>
        </>
      }
    >
      <div className="sk:-mx-4 sk:-mt-4 sk:mb-4 sk:flex sk:items-center sk:gap-2 sk:border-b sk:border-rule sk:px-4 sk:py-3">
        <h2 className="sk:m-0 sk:flex-1 sk:text-sm sk:font-semibold sk:text-fg">
          Manual Order
        </h2>
        <Button
          variant="ghost"
          size="sm"
          shape="circle"
          aria-label="Close"
          onPress={() => close()}
        >
          <X size={12} weight="bold" />
        </Button>
      </div>
      {saving && <LoaderBar content="Saving" />}
      <form
        id={formId}
        autoComplete="skupreme-off"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="sk:mb-3">
          <MuteLabel className="sk:mb-1 sk:block">
            Search Existing Customers
          </MuteLabel>
          <CustomerSearchInput top={false} onResultClick={onUserSelect} />
        </div>
        <div className="sk:grid sk:gap-x-3 sk:md:grid-cols-4">
          <ControlledTextFormField
            error={errors?.name}
            label="Recipient"
            name="name"
            defaultValue={label?.name}
            control={control}
            required
            autoComplete="skupreme-off"
            fieldClass="sk:md:col-span-2"
            inputRight={
              <Button
                size="sm"
                className="sk:ml-1 sk:shrink-0"
                onPress={() => setShowAddressPopup(true)}
              >
                Paste Address
              </Button>
            }
          />
          <ControlledTextFormField
            label="Company"
            name="company"
            control={control}
            autoComplete="skupreme-off"
          />
          {/* @ts-expect-error - TS2740: Type '{ error: FieldError | undefined; label: string; inputC... */}
          <NoSpecialCharactedInput
            error={errors?.order_id}
            label="Order ID"
            defaultValue={padStart(lodashToString(random(9999)), 4, '0')}
            name="order_id"
            control={control}
            required
            autoComplete="skupreme-off"
            maxLength={40}
            className="sk:pl-11!"
            inputLeft={
              <span className="sk:pointer-events-none sk:absolute sk:left-3 sk:z-1 sk:text-dense sk:text-fg-tertiary">
                OID-
              </span>
            }
          />
        </div>
        <div className="sk:grid sk:gap-x-3 sk:md:grid-cols-3">
          <ControlledTextFormField
            error={errors?.address_line_one}
            label="Address Line 1"
            name="address_line_one"
            defaultValue={label?.address?.addressLine1}
            control={control}
            autoComplete="skupreme-off"
            required
          />
          <ControlledTextFormField
            error={errors?.address_line_two}
            placeholder="Address Line 2"
            label="Address Line 2"
            helpText={
              'Avoid using special characters - the carrier does not support them. ' +
              "They'll be automatically removed. Use standard abbreviations like " +
              '"STE" or "UNIT" instead.'
            }
            name="address_line_two"
            control={control}
            autoComplete="skupreme-off"
          />
          <ControlledTextFormField
            error={errors?.city}
            label="City"
            name="city"
            defaultValue={label?.address?.city}
            control={control}
            required
            autoComplete="skupreme-off"
          />
        </div>
        <div className="sk:grid sk:gap-x-3 sk:md:grid-cols-3">
          <ControlledTextFormField
            error={errors?.state}
            label="State"
            name="state"
            defaultValue={label?.address?.stateOrRegion}
            control={control}
            required
          />
          <ControlledTextFormField
            error={errors?.zip_code}
            label="Zip Code"
            name="zip_code"
            defaultValue={label?.address?.postalCode}
            control={control}
            autoComplete="skupreme-off"
            required
          />
          {/* @ts-expect-error - TS2740: Type '{ error: FieldError | undefined; label: string; classN... */}
          <CountryDropdownFormField
            error={errors?.country}
            label="Country"
            required
            name="country"
            autoComplete="skupreme-off"
            control={control}
            dropdownChanged={handleCountryChange}
            options={dropdownOptionsIncludeFlag(regionData)}
          />
        </div>
        <div className="sk:grid sk:gap-x-3 sk:md:grid-cols-3">
          <ControlledPhoneFormField
            error={errors?.phone}
            label="Phone Number"
            countryCode={countryCode}
            name="phone"
            // @ts-expect-error - TS2322: Type '{ error: FieldError | undefined; label: string; countryCode: undefined; na...
            autoComplete="skupreme-off"
            defaultValue={label?.phone}
            control={control}
          />
          <ControlledTextFormField
            error={errors?.email}
            label="Email"
            name="email"
            defaultValue={label?.email}
            control={control}
            autoComplete="skupreme-off"
          />
          <div>
            <MarketplacesDropdown
              key={marketplaceOptions.map(o => o.value).join(',')}
              control={control}
              name="marketplace_id"
              marketplaces={marketplaceOptions || []}
              label="Source"
              placeholder="Select source"
              error={errors?.marketplace_id}
              required={false}
            />
            {errors?.marketplace_id && (
              <span
                role="alert"
                className="sk:-mt-4 sk:mb-4 sk:block sk:text-xs sk:text-danger"
              >
                {errors.marketplace_id.message}
              </span>
            )}
          </div>
        </div>
        <div className="sk:flex sk:items-center">
          <StyledCheckbox
            onChange={(_e, { checked }) => setIsInternational(!!checked)}
            checked={isInternational}
            label="International Order"
          />
          <FormHelpToolTip helpText="Selecting international order will display items having inventory across all regions." />
        </div>
        <div className="sk:my-4">
          <PageHeaderText>Order Line Items</PageHeaderText>
        </div>
        <Items
          control={control}
          customItemRemove={customItemRemove}
          customItemsFields={customItemsFields}
          errors={errors}
          fields={fields}
          items={items}
          onResultClick={onResultClick}
          regionId={country}
          removeItem={removeItem}
          isInternational={isInternational}
        />
      </form>
      {showAddressPopup && (
        <PasteAddressPPopup
          open={showAddressPopup}
          extractAddress={extractAddress}
          closeModal={result => setShowAddressPopup(!!result || false)}
          defaultName={name}
        />
      )}
    </Modal>
  );
}

export default ManualOrderPopup;
